import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Provider } from '../types';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

interface ProvidersDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: Provider | null;
  onEdit: () => void;
  canManage: boolean;
}

export const ProvidersDetailModal: React.FC<ProvidersDetailModalProps> = ({
  isOpen,
  onClose,
  provider,
  onEdit,
  canManage,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === 'tr' ? tr : enUS;

  if (!isOpen || !provider) return null;

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return '-';
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale });
  };

  const DetailRow = ({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={`py-3 border-b border-gray-100 dark:border-gray-700 ${className}`}>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value || '-'}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-semibold">
                {provider.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {provider.name}
              </h2>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  provider.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {provider.isActive ? t('common.active') : t('common.inactive')}
                </span>
                {provider.providerType === 'whatsapp' && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    WhatsApp
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <dl className="space-y-1">
            <DetailRow
              label={t('providers.contactEmail')}
              value={provider.contactEmail}
            />
            <DetailRow
              label={t('providers.contactPhone')}
              value={provider.contactPhone}
            />
            <DetailRow
              label={t('providers.address')}
              value={provider.address}
            />
            <DetailRow
              label={t('providers.pricingModel')}
              value={provider.pricingModel === 'fixed' ? t('providers.fixed') : t('providers.manual')}
            />
            <DetailRow
              label={t('providers.paymentMode')}
              value={provider.paymentMode === 'lump_sum' ? t('providers.lumpSum') : t('providers.perTransfer')}
            />
            <DetailRow
              label={t('providers.balance')}
              value={
                <span className={`font-medium ${
                  Number(provider.balance) > 0
                    ? 'text-green-600'
                    : Number(provider.balance) < 0
                      ? 'text-red-600'
                      : ''
                }`}>
                  {Number(provider.balance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                </span>
              }
            />
            <DetailRow
              label={t('providers.notes')}
              value={provider.notes}
            />
            <DetailRow
              label={t('common.createdAt')}
              value={formatDate(provider.createdAt)}
            />
            <DetailRow
              label={t('common.updatedAt')}
              value={formatDate(provider.updatedAt)}
              className="border-b-0"
            />
          </dl>
        </div>

        {/* Footer */}
        {canManage && (
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
            <button
              onClick={() => { onClose(); navigate(`/app/providers/${provider.id}/users`); }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              {t('users.title')}
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.edit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
